import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import VectorSource from 'ol/source/Vector';
import VectorLayer from 'ol/layer/Vector';
import { Circle as CircleStyle, Fill, Stroke, Style } from 'ol/style';

import { unByKey } from 'ol/Observable';
import { Coordinate } from 'ol/coordinate';
import Map, { FrameState } from 'ol/Map';
import BaseEvent from 'ol/events/Event';

/**
 * Spring-inspired easing for organic motion (Josh Comeau style)
 * Creates a slight overshoot then settles - feels more "alive"
 * Uses an exponential ease-in-out for snappy, punchy feel
 */
const springEase = (t: number): number => {
  if (t === 0) return 0;
  if (t === 1) return 1;
  return t < 0.5
    ? Math.pow(2, 20 * t - 10) / 2
    : (2 - Math.pow(2, -20 * t + 10)) / 2;
};

/** Smoother ease-out for the fade */
const easeOutQuart = (t: number): number => 1 - Math.pow(1 - t, 4);

/** Read the --primary CSS variable and convert HSL to RGB */
const getPrimaryColor = (): { r: number; g: number; b: number } => {
  const fallback = { r: 0, g: 122, b: 128 }; // Default teal

  try {
    const hslValue = getComputedStyle(document.documentElement)
      .getPropertyValue('--primary')
      .trim();

    if (!hslValue) return fallback;

    // Parse "191 100% 24%" format
    const parts = hslValue.split(/\s+/);
    if (parts.length < 3) return fallback;

    const h = parseFloat(parts[0]) / 360;
    const s = parseFloat(parts[1]) / 100;
    const l = parseFloat(parts[2]) / 100;

    // HSL to RGB conversion
    const hue2rgb = (p: number, q: number, t: number): number => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;

    return {
      r: Math.round(hue2rgb(p, q, h + 1 / 3) * 255),
      g: Math.round(hue2rgb(p, q, h) * 255),
      b: Math.round(hue2rgb(p, q, h - 1 / 3) * 255),
    };
  } catch {
    return fallback;
  }
};

interface RingConfig {
  delay: number;
  maxRadius: number;
  strokeWidth: number;
}

export const addPulseToCoordinate = (
  map: Map,
  coordinate: Coordinate,
  times = 3
): void => {
  const flashFeature = new Feature<Point>(new Point(coordinate));
  const flashLayerSource = new VectorSource<Feature<Point>>();
  let completedAnimations = 0;

  flashLayerSource.addFeature(flashFeature);

  const flashLayer = new VectorLayer<Feature<Point>>({
    source: flashLayerSource,
  });

  map.addLayer(flashLayer);

  const color = getPrimaryColor();
  const duration = 1400; // Longer duration for more visible pulse
  const startRadius = 6;

  // Three staggered rings - tighter delays for more "juice"
  const rings: RingConfig[] = [
    { delay: 0, maxRadius: 32, strokeWidth: 2.5 },
    { delay: 100, maxRadius: 44, strokeWidth: 2 },
    { delay: 200, maxRadius: 56, strokeWidth: 1.5 },
  ];

  let start: number = Date.now();

  const animate = (event: BaseEvent | Event): void => {
    const frameState = (event as { frameState?: FrameState }).frameState;
    if (!frameState) return;

    const elapsed = frameState.time - start;

    // Check if cycle complete
    if (elapsed > duration + rings[rings.length - 1].delay + 100) {
      completedAnimations += 1;
      if (completedAnimations >= times) {
        unByKey(listenerKey);
        map.removeLayer(flashLayer);
        return;
      }
      start = frameState.time;
    }

    const styles: Style[] = [];

    // Center dot - quick pop then fade (the "anchor" point)
    const dotProgress = Math.min(1, elapsed / (duration * 0.6));
    const dotScale = dotProgress < 0.3 ? springEase(dotProgress / 0.3) : 1;
    const dotOpacity = dotProgress < 0.3 ? 0.7 : 0.7 * (1 - easeOutQuart((dotProgress - 0.3) / 0.7));
    const dotRadius = startRadius * (0.8 + 0.4 * dotScale);

    if (dotOpacity > 0.01) {
      styles.push(
        new Style({
          image: new CircleStyle({
            radius: dotRadius,
            fill: new Fill({
              color: `rgba(${color.r}, ${color.g}, ${color.b}, ${Math.max(0, dotOpacity)})`,
            }),
          }),
        })
      );
    }

    // Soft glow behind rings (creates depth)
    const glowProgress = Math.min(1, elapsed / duration);
    const glowOpacity = 0.15 * (1 - easeOutQuart(glowProgress));
    const glowRadius = startRadius + 20 * easeOutQuart(glowProgress);

    if (glowOpacity > 0.01) {
      styles.push(
        new Style({
          image: new CircleStyle({
            radius: glowRadius,
            fill: new Fill({
              color: `rgba(${color.r}, ${color.g}, ${color.b}, ${glowOpacity})`,
            }),
          }),
        })
      );
    }

    // Expanding rings with staggered timing
    for (const ring of rings) {
      const ringElapsed = elapsed - ring.delay;
      if (ringElapsed < 0) continue;

      const progress = Math.min(1, ringElapsed / duration);
      // Use quart easing for smooth deceleration (Josh's "ease" preference)
      const radiusProgress = easeOutQuart(progress);
      // Opacity fades faster at the end for crisp finish
      const opacity = Math.pow(1 - progress, 1.5);

      if (opacity <= 0.02) continue;

      const radius = startRadius + (ring.maxRadius - startRadius) * radiusProgress;
      // Stroke thins as it expands (more organic)
      const strokeWidth = ring.strokeWidth * (0.4 + 0.6 * (1 - radiusProgress));

      styles.push(
        new Style({
          image: new CircleStyle({
            radius,
            stroke: new Stroke({
              color: `rgba(${color.r}, ${color.g}, ${color.b}, ${opacity})`,
              width: strokeWidth,
            }),
          }),
        })
      );
    }

    flashLayer.setStyle(styles);
    map.render();
  };

  const listenerKey = map.on('postrender', animate);
};
